import { nanoid } from "nanoid";
import { useCallback, useLayoutEffect, useRef } from "react";
import { cacheToolOutput } from "../../hooks/useToolOutput";
import { playClick } from "../../lib/sounds";
import type { ChatProps, ChatSendPayload } from "./chatTypes";
import { claimQueued, type QueuedMessage } from "./queueStorage";
import { useChatQueue } from "./useChatQueue";

type SubmitOptions = {
  queue?: boolean;
  queuedId?: string;
  restoreOnError?: string;
};

type Options = Pick<
  ChatProps,
  "onSend" | "onInterrupt" | "onProvideToolResult"
> & {
  canWrite: boolean;
  chatId: string | null;
  active: boolean;
  queuePaused: boolean;
  editing: boolean;
  chatModel: string | null;
  onRestoreDraft: (payload: ChatSendPayload, draft: string) => void;
  onAction: () => void;
};

export function useChatActions(options: Options) {
  const { canWrite, chatId, active, queuePaused, editing } = options;
  // Recorder and tool callbacks can outlive the render that created them.
  const actionsRef = useRef(options);
  useLayoutEffect(() => {
    actionsRef.current = options;
    return () => {
      actionsRef.current = { ...options, canWrite: false };
    };
  });

  // Transcript context subscriptions depend on action callback identity.
  const send = useCallback((payload: ChatSendPayload, onError: () => void) => {
    const { canWrite, chatModel, onSend, onAction } = actionsRef.current;
    if (!canWrite) return false;
    onSend(
      { ...payload, chatModel: payload.chatModel ?? chatModel ?? undefined },
      onError
    );
    onAction();
    return true;
  }, []);
  const releaseQueued = (card: QueuedMessage, markFailed: () => void) => {
    const { id: _id, state: _state, ...payload } = card;
    if (send(payload, markFailed)) playClick();
  };
  const [queue, setQueue] = useChatQueue(
    chatId,
    active || !canWrite || queuePaused,
    editing,
    releaseQueued
  );

  const submit = useCallback(
    (payload: ChatSendPayload, options: SubmitOptions = {}) => {
      const {
        canWrite,
        active,
        queuePaused,
        chatId,
        chatModel,
        onRestoreDraft,
      } = actionsRef.current;
      if (!canWrite) return false;
      if (options.queue && (active || queuePaused)) {
        setQueue((q) => [
          ...q,
          {
            id: nanoid(),
            ...payload,
            chatModel: payload.chatModel ?? chatModel ?? undefined,
          },
        ]);
        return true;
      }
      if (options.queuedId) {
        const claimed = !chatId || claimQueued(chatId, options.queuedId);
        setQueue((q) => q.filter((item) => item.id !== options.queuedId));
        if (!claimed) return false;
      }
      return send(payload, () => {
        if (options.restoreOnError !== undefined)
          onRestoreDraft(payload, options.restoreOnError);
      });
    },
    [send, setQueue]
  );

  const interrupt = () => {
    const { canWrite, onInterrupt } = actionsRef.current;
    if (!canWrite) return;
    onInterrupt();
  };

  const toolResultsInFlight = useRef(new Map<string, Promise<boolean>>());
  const provideToolResult = useCallback(
    (
      ...[entryId, output, isError, options]: Parameters<
        ChatProps["onProvideToolResult"]
      >
    ) => {
      const { canWrite, chatId, chatModel, onProvideToolResult, onAction } =
        actionsRef.current;
      if (!canWrite || !chatId) return null;
      const pending = toolResultsInFlight.current.get(entryId);
      if (pending) return pending;
      cacheToolOutput(entryId, output);
      const result = onProvideToolResult(entryId, output, isError, {
        ...options,
        chatModel: options?.chatModel ?? chatModel ?? undefined,
      });
      toolResultsInFlight.current.set(entryId, result);
      const clear = () => toolResultsInFlight.current.delete(entryId);
      void result.then(clear, clear);
      onAction();
      return result;
    },
    []
  );

  const cancelQueued = (id: string) => {
    if (!actionsRef.current.canWrite) return;
    setQueue((q) => q.filter((item) => item.id !== id));
  };
  const editQueued = (id: string, text: string) => {
    if (!actionsRef.current.canWrite || !text.trim()) return;
    setQueue((q) =>
      q.map((item) =>
        item.id === id ? { ...item, text, state: undefined } : item
      )
    );
  };

  return {
    submit,
    interrupt,
    provideToolResult,
    queue,
    cancelQueued,
    editQueued,
  };
}
